import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { FooterMenu } from "@qivia/ui";
import { Page } from "@qivia/ui/src/designSystem/components/driverAppV2/Page";
import { TooltipHover } from "@qivia/ui/src/designSystem/components/TooltipHover";
import {
  AccountBalanceOutlined,
  ArticleOutlined,
  CallOutlined,
  CreditCardOutlined,
  // DirectionsCarOutlined,
  HomeOutlined,
  // PersonOutlined,
  RouteOutlined,
} from "@qivia/ui/src/designSystem/materialUi/materialUi";
import { useAppSelector } from "../../redux/hooks";
import { selectActiveDriverByPhone } from "../qrCode/menuSlice";
import { isAppV2Enabled } from "./appV2Access";

type Tab = {
  path: string;
  labelKey: string;
  titleKey: string;
  icon: ReactNode;
  tooltipId?: string;
};

const CARD_TAB_TOOLTIP_ID = "CardTabTooltip";

const TABS: Tab[] = [
  {
    path: "/home",
    labelKey: "appV2.footerMenu.home",
    titleKey: "appV2.home.title",
    icon: <HomeOutlined />,
  },
  {
    path: "/refunds",
    labelKey: "appV2.footerMenu.refunds",
    titleKey: "appV2.refunds.title",
    icon: <AccountBalanceOutlined />,
  },
  {
    path: "/card",
    labelKey: "appV2.footerMenu.card",
    titleKey: "appV2.footerMenu.card",
    icon: <CreditCardOutlined />,
    tooltipId: CARD_TAB_TOOLTIP_ID,
  },
  {
    path: "/operations",
    labelKey: "appV2.footerMenu.operations",
    titleKey: "appV2.operations.title",
    icon: <ArticleOutlined />,
  },
  {
    path: "/trips",
    labelKey: "appV2.footerMenu.trips",
    titleKey: "appV2.trips.title",
    icon: <RouteOutlined />,
  },
  // {
  //   path: "/vehicle",
  //   labelKey: "appV2.footerMenu.vehicle",
  //   titleKey: "appV2.vehicle.title",
  //   icon: <DirectionsCarOutlined />,
  // },
  // {
  //   path: "/profile",
  //   labelKey: "appV2.footerMenu.profile",
  //   titleKey: "appV2.profile.title",
  //   icon: <PersonOutlined />,
  // },
];

export const AppV2Layout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { driver } = useAppSelector(selectActiveDriverByPhone);
  const cardTooltipKey = driver ? `cardTabTooltipSeen:${driver.uuid}` : null;
  const [isCardTooltipDismissed, setIsCardTooltipDismissed] = useState(false);
  const isCardTooltipVisible =
    !!cardTooltipKey &&
    !isCardTooltipDismissed &&
    localStorage.getItem(cardTooltipKey) !== "true";

  if (!isAppV2Enabled) return <Navigate to="/menu" replace />;

  const items = TABS.map((tab) => ({
    label: t(tab.labelKey),
    icon: tab.icon,
    isSelected: pathname === tab.path,
    onClick: () => navigate(tab.path),
    tooltipId: tab.tooltipId,
  }));

  const current = TABS.find((tab) => tab.path === pathname);

  const topActions = [
    // {
    //   label: t("appV2.header.card"),
    //   icon: <CreditCardOutlined />,
    //   onClick: () => navigate("/menu"),
    // },
    {
      label: t("appV2.header.support"),
      icon: <CallOutlined />,
      onClick: () => navigate("/help"),
    },
  ];

  const isFullPage = pathname === "/card";

  return (
    <Page
      title={current ? t(current.titleKey) : undefined}
      topActions={topActions}
      footerMenu={<FooterMenu items={items} />}
      tone={isFullPage ? "dark" : "light"}
      isFullPage={isFullPage}
    >
      {isCardTooltipVisible && (
        <TooltipHover
          id={CARD_TAB_TOOLTIP_ID}
          place="top"
          isOpen
          badge={t("appV2.cardTooltip.badge")}
          title={t("appV2.cardTooltip.title")}
          content={t("appV2.cardTooltip.content")}
          onDismiss={() => {
            localStorage.setItem(cardTooltipKey, "true");
            setIsCardTooltipDismissed(true);
          }}
        />
      )}
      <Outlet />
    </Page>
  );
};
